import { useLocation } from "react-router-dom";
import React, { useEffect } from 'react';
import { useQuery } from "@tanstack/react-query";
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from "./components/ui/toaster";
import { GetRequestVerificationToken, GetUser } from "./components/api/queries";
import Routes from "./components/routes/Routes";
import { useNavigation } from "./components/hooks/useNavigation";
import LoadingSkeletonMain from "./components/LoadingSkeleton/LoadingSkeleton";
import { useQueryLogger } from "./components/hooks/useQueryLogger";
import AdminTools from "./components/pages/admin/tools";
import { NavigationProvider } from "./components/context/NavigationContext";
// for stage 2
import { AccumulateProvider } from "./components/context/AccumulateContext";
// end for stage 2
import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';
import { JSErrors } from '@newrelic/browser-agent/features/jserrors';
import { Spa } from '@newrelic/browser-agent/features/spa';
import { Metrics } from '@newrelic/browser-agent/features/metrics';
import { setLocalStorage } from "./utils/LocalStorageHelper";
import showConsole from '@/utils/showConsole';


// Official TanStack production devtools lazy loading
const ReactQueryDevtoolsProduction = React.lazy(() =>
  import('@tanstack/react-query-devtools/build/modern/production.js').then(
    (d) => ({
      default: d.ReactQueryDevtools,
    }),
  ),
);

const newRelicId = process.env.REACT_APP_NEWRELIC_ID

if (!newRelicId) {
  throw new Error("NewRelic ID is not set")
}

const newRelicOptions = {
  init: {
    distributed_tracing: {
      enabled: true
    },
    privacy: {
      cookies_enabled: true
    },
    ajax: {
      deny_list: ["bam.nr-data.net"]
    }
  },
  info: {
    beacon: "bam.nr-data.net",
    errorBeacon: "bam.nr-data.net",
    licenseKey: "NRJS-e0cb5df74867acf7231",
    applicationID: newRelicId,
    sa: 1
  },
  loader_config:
  {
    accountID: "4347624",
    trustKey: "284075",
    agentID: newRelicId,
    licenseKey: "NRJS-e0cb5df74867acf7231",
    applicationID: newRelicId
  },
  features: [
    JSErrors, Spa, Metrics
  ]
}


if (process.env.NODE_ENV === 'development') {
  // avoid printing secrets in logs in production
  console.log('NewRelic (sanitized):', {
    applicationID: newRelicOptions.info?.applicationID,
    agentID: newRelicOptions.loader_config?.agentID,
  });
}

export const newrelic = new BrowserAgent(newRelicOptions)

// google analytics gtm setup
declare global {
  interface Window {
    dataLayer?: any[];
  }
}

function useGTMPageView() {
  const location = useLocation();
  useEffect(() => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "pageview",
        page: location.pathname + location.search,
      });
    }
  }, [location]);
}


export default function App() {

  useGTMPageView();
  // const [showDevtools, setShowDevtools] = React.useState(false);
  const showDevtoolsFlag =
    process.env.NODE_ENV === 'development' ||
    window.location.search.includes('version=dev');
    // State for toggling production devtools
const [showDevtools, setShowDevtools] = React.useState(false);

  const { data: userData } = useQuery({
    queryKey: ["user"],
    queryFn: GetUser,
  });


  useEffect(() => {
    const errorHandler = (event: ErrorEvent) => {
      const error = new Error(event.message);
      newrelic.noticeError(error, {
        errorStack: event.error?.stack,
        errorUrl: window.location.href,
        user: userData ? userData : 'No user data available.'
      });
    };
    window.addEventListener('error', errorHandler);
    return () => {
      window.removeEventListener('error', errorHandler);
    };
  }, [userData]);

  useEffect(() => {
    if (userData?.result) {
      // Optionally store user data in localStorage
      setLocalStorage("user", userData);

      // Set New Relic user and custom attributes (only if fields exist)
      newrelic.setUserId(
        `${userData.result.fullname ?? userData.result.firstname ?? "Unknown"} | ${userData.result.id ?? "Unknown"}`
      );
      newrelic.setCustomAttribute('UserFullName', userData.result.fullname ?? userData.result.firstname ?? "Unknown");
      newrelic.setCustomAttribute('UserId', userData.result.id ?? "Unknown");

      // Only set account info if it exists
      if (userData.result.account) {
        newrelic.setCustomAttribute('AccountId', userData.result.account.accountid ?? "Unknown");
        newrelic.setCustomAttribute('AccountName', userData.result.account.companyname ?? "Unknown");
      }
    }
  }, [userData]);

  // [12-May-2026] Marketing Attribution: Capture Google Ads params & referrer on landing
  useEffect(() => {
    // Google Ads params → localStorage (30-day attribution window)
    const params = new URLSearchParams(window.location.search);
    const adKeys = ['gclid', 'gad_campaignid'] as const;
    const adParams: Record<string, string> = {};

    adKeys.forEach(key => {
      const val = params.get(key);
      if (val) adParams[key] = val;
    });

    if (Object.keys(adParams).length > 0) {
      localStorage.setItem('gads_attribution', JSON.stringify({ ...adParams, capturedAt: Date.now() }));
    }

    // Referrer → sessionStorage (survives SPA navigation, clears on tab close)
    if (!sessionStorage.getItem('original_referrer')) {
      const referrer = document.referrer;
      if (referrer && !referrer.includes(window.location.hostname)) {
        sessionStorage.setItem('original_referrer', referrer);
      } else if (!referrer) {
        sessionStorage.setItem('original_referrer', 'direct');
      }
    }
  }, []);

  React.useEffect(() => {
    // @ts-expect-error - Adding global function for debugging
    window.toggleDevtools = () => setShowDevtools((old) => !old);

    // Log helpful debugging info - only in development
    // if (process.env.NODE_ENV === 'development') {
    if (showConsole) {
      console.log('🔧 React Query DevTools Setup:');
      console.log('   - Development: DevTools should be visible in bottom-right corner');
      console.log('   - Production: Use AdminTools floating component or window.toggleDevtools()');
      console.log('   - Query Logger: Use window.queryLogger.logAllQueries() for debug info');
    }
  }, []);



  //making sure nav is available before rendering the app
  const { isFetching, isLoading } = useNavigation();

  // Enable query logging for debugging
  const queryLogger = useQueryLogger();

  // Make query logger available globally for debugging
  (window as any).queryLogger = queryLogger;

  const RequestVerificationToken = useQuery({
    queryKey: ["RequestVerificationToken"],
    queryFn: () =>
      GetRequestVerificationToken().then((res) => {
        const xtoken = res.data.documentElement.querySelector("input[name=__RequestVerificationToken]").value;
        sessionStorage.setItem("x-token", xtoken);
        return xtoken;
      }),
    staleTime: 60 * 1000 * 60,
    gcTime: 60 * 1000 * 60,
    retry: 1,
  });




  return (
    <NavigationProvider>
      {/* <AccumulateProvider> is for stage2 */}
      <AccumulateProvider>
        <Toaster />
        {RequestVerificationToken.data && (
          <input className="hidden" id="x-token" value={RequestVerificationToken.data} placeholder="token" readOnly={true} />
        )}
        {/* Official TanStack DevTools - Development */}


        {showDevtoolsFlag && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}


        {/* Official TanStack DevTools - Production */}
        {showDevtoolsFlag && (
          <React.Suspense fallback={<div className="devtools-loading">Loading DevTools...</div>}>
            <ReactQueryDevtoolsProduction />
          </React.Suspense>
        )}

        <div className="flex flex-col bg-black no-scrollbar overflow-x-hidden relative">
          {isLoading || isFetching ? (


            <LoadingSkeletonMain />


          ) : (
            <Routes />
          )}
        </div>

        {/* Admin Tools - Only visible to Administrators */}
        <AdminTools
          showDevtools={showDevtools}
          onToggleDevtools={() => setShowDevtools(!showDevtools)}
        />
      </AccumulateProvider>
    </NavigationProvider>
  );
}

